import { createTransaction } from "@tanstack/db";
import {
  enqueueSave,
  markCascadedWrites,
  outbox,
  persistQueuedSave,
  reportWriteError,
  trackWrite,
} from "../core/outbox";
import { markWriteErrorReported, OptimisticWriteError, UnconfirmedSaveError } from "../core/write-errors";

export type WriteOptions = { signal: AbortSignal; context: { writeErrorHandled: true } };

export function defineWrite<I>(spec: {
  coalesce?: boolean;
  timeoutMs?: number | null;
  optimistic: (input: I) => void;
  persist: (input: I, options: WriteOptions) => Promise<unknown>;
}) {
  return (input: I) => {
    const transaction = createTransaction({
      autoCommit: false,
      mutationFn: async ({ transaction }) => {
        try {
          await persistQueuedSave(transaction);
        } catch (error) {
          if (error instanceof UnconfirmedSaveError) {
            const collections = [...new Set(transaction.mutations.map((mutation) => mutation.collection))];
            await persistWithTimeout(async () => {
              await Promise.allSettled(collections.map(async (collection) => collection.utils.refetch?.()));
            }).catch(() => {});
          }
          markCascadedWrites(transaction, error);
          reportWriteError(transaction, error);
          throw error;
        }
      },
    });
    try {
      transaction.mutate(() => {
        spec.optimistic(input);
        for (const mutation of [...transaction.mutations]) {
          const derive = mutation.collection.utils.derive;
          if (mutation.type !== "delete" && derive) {
            const row = derive(mutation.modified);
            mutation.collection.update(mutation.key, (draft) => {
              Object.assign(draft, row);
            });
          }
        }
      });
    } catch (error) {
      void transaction.isPersisted.promise.catch(() => {});
      const cause = new OptimisticWriteError(error);
      markWriteErrorReported(cause);
      transaction.error = { message: cause.message, error: cause };
      transaction.rollback({ isSecondaryRollback: true });
      reportWriteError(transaction, error, { cascades: false });
      return transaction;
    }
    if (!transaction.mutations.length) {
      void transaction.commit();
      return transaction;
    }
    enqueueSave(
      transaction,
      spec,
      () =>
        persistWithTimeout(
          (signal) => spec.persist(input, { signal, context: { writeErrorHandled: true } }),
          spec.timeoutMs,
        ),
      spec.coalesce ?? false,
    );
    trackWrite(transaction);
    outbox.execute(() => {
      void transaction.commit().catch(() => {});
      return transaction;
    });
    return transaction;
  };
}

async function persistWithTimeout(
  persist: (signal: AbortSignal) => Promise<unknown>,
  timeoutMs: number | null = 15_000,
): Promise<void> {
  if (timeoutMs === null) {
    await persist(new AbortController().signal);
    return;
  }
  const signal = AbortSignal.timeout(timeoutMs);
  let aborted: () => void = () => {};
  const timeout = new Promise<never>((_, reject) => {
    aborted = () => reject(new UnconfirmedSaveError());
    signal.addEventListener("abort", aborted, { once: true });
  });
  try {
    await Promise.race([timeout, persist(signal)]);
  } finally {
    signal.removeEventListener("abort", aborted);
  }
}
